/**
 * Partner-Finding API
 *
 * Search for challenge partners, send/accept/decline partner requests.
 */

import type {
  PartnerCandidate,
  PartnerRequest,
  PartnerRequestStatus,
  PartnerSearchFilters,
  ApiResponse,
} from '../types'
import { mockDb, generateId, delay } from './mockDb'
import { success, error } from './client'

const SESSION_KEY = 'docurious-session'

function getCurrentUserId(): string | null {
  const stored = localStorage.getItem(SESSION_KEY)
  if (!stored) return null
  try {
    return JSON.parse(stored).userId
  } catch {
    return null
  }
}

interface UserRecord {
  id: string
  displayName: string
  avatarUrl?: string
  interests: string[]
  isUnder13: boolean
  accountStatus: string
  [key: string]: unknown
}

interface CommunityMembershipRecord {
  communityId: string
  userId: string
  status: string
  [key: string]: unknown
}

interface PrivacySettingsRecord {
  userId: string
  showInPartnerFinder: boolean
  [key: string]: unknown
}

interface ChallengeRecord {
  id: string
  title: string
  coverImageUrl?: string
  soloGroup: string
  [key: string]: unknown
}

interface PartnerRequestRecord {
  id: string
  challengeId: string
  senderUserId: string
  recipientUserId: string
  status: PartnerRequestStatus
  message?: string
  expiresAt: string
  createdAt: string
  updatedAt: string
  [key: string]: unknown
}

export const partnerApi = {
  /**
   * Search for partner candidates for a specific challenge
   */
  async searchCandidates(filters: PartnerSearchFilters): Promise<ApiResponse<PartnerCandidate[]>> {
    await delay(150)

    const currentUserId = getCurrentUserId()
    if (!currentUserId) return error('Not authenticated')

    const allUsers = mockDb.getAll<UserRecord>('users')

    // Get current user's communities for mutual count
    const allMemberships = mockDb.getAll<CommunityMembershipRecord>('communityMemberships')
    const myMemberships = allMemberships
      .filter(m => m.userId === currentUserId && m.status === 'active')
      .map(m => m.communityId)

    // Get privacy settings
    const privacySettings = mockDb.getAll<PrivacySettingsRecord>('privacySettings')

    const candidates: PartnerCandidate[] = allUsers
      .filter(u => {
        if (u.id === currentUserId) return false
        if (u.accountStatus !== 'active') return false
        if (u.isUnder13) return false // Under-13 users can't use partner finder

        // Check if user has opted into partner finder
        const settings = privacySettings.find(s => s.userId === u.id)
        if (settings && !settings.showInPartnerFinder) return false

        // Search query
        if (filters.query) {
          const q = filters.query.toLowerCase()
          if (!u.displayName.toLowerCase().includes(q)) return false
        }

        // Interest filter
        if (filters.interests?.length) {
          const hasMatch = filters.interests.some(i => u.interests?.includes(i))
          if (!hasMatch) return false
        }

        return true
      })
      .map(u => {
        const userMemberships = allMemberships
          .filter(m => m.userId === u.id && m.status === 'active')
          .map(m => m.communityId)
        const mutualCommunities = myMemberships.filter(c => userMemberships.includes(c)).length

        return {
          id: u.id,
          displayName: u.displayName,
          avatarUrl: u.avatarUrl,
          interests: u.interests ?? [],
          challengesCompleted: 0,
          level: 1,
          mutualCommunities,
        }
      })
      .slice(0, 20)

    return success(candidates)
  },

  /**
   * Send a partner request
   */
  async sendRequest(data: {
    challengeId: string
    recipientUserId: string
    message?: string
  }): Promise<ApiResponse<PartnerRequest>> {
    await delay(100)

    const senderId = getCurrentUserId()
    if (!senderId) return error('Not authenticated')

    const now = new Date().toISOString()
    const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()

    // Check for existing pending request
    const existing = mockDb.getAll<PartnerRequestRecord>('partnerRequests').find(
      r => r.challengeId === data.challengeId &&
        r.senderUserId === senderId &&
        r.recipientUserId === data.recipientUserId &&
        r.status === 'pending',
    )
    if (existing) return error('You already have a pending request to this user for this challenge')

    const record = mockDb.insert<PartnerRequestRecord>('partnerRequests', {
      id: generateId(),
      challengeId: data.challengeId,
      senderUserId: senderId,
      recipientUserId: data.recipientUserId,
      status: 'pending',
      message: data.message,
      expiresAt,
      createdAt: now,
      updatedAt: now,
    })

    return success(populateRequest(record))
  },

  /**
   * Get my partner requests (sent and received)
   */
  async getMyRequests(): Promise<ApiResponse<PartnerRequest[]>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) return error('Not authenticated')

    const all = mockDb.getAll<PartnerRequestRecord>('partnerRequests')
    const mine = all.filter(r => r.senderUserId === userId || r.recipientUserId === userId)

    return success(mine.map(populateRequest))
  },

  /**
   * Respond to a partner request
   */
  async respondToRequest(
    requestId: string,
    decision: 'accepted' | 'declined',
  ): Promise<ApiResponse<PartnerRequest>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) return error('Not authenticated')

    const record = mockDb.getById<PartnerRequestRecord>('partnerRequests', requestId)
    if (!record) return error('Request not found')
    if (record.recipientUserId !== userId) return error('Not authorized')
    if (record.status !== 'pending') return error('Request is no longer pending')

    const updated = mockDb.update<PartnerRequestRecord>('partnerRequests', requestId, {
      status: decision,
      updatedAt: new Date().toISOString(),
    })

    return success(populateRequest(updated))
  },

  /**
   * Cancel a sent partner request
   */
  async cancelRequest(requestId: string): Promise<ApiResponse<PartnerRequest>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) return error('Not authenticated')

    const record = mockDb.getById<PartnerRequestRecord>('partnerRequests', requestId)
    if (!record) return error('Request not found')
    if (record.senderUserId !== userId) return error('Not authorized')
    if (record.status !== 'pending') return error('Request is no longer pending')

    const updated = mockDb.update<PartnerRequestRecord>('partnerRequests', requestId, {
      status: 'cancelled',
      updatedAt: new Date().toISOString(),
    })

    return success(populateRequest(updated))
  },
}

function populateRequest(record: PartnerRequestRecord): PartnerRequest {
  const sender = mockDb.getById<UserRecord>('users', record.senderUserId)
  const recipient = mockDb.getById<UserRecord>('users', record.recipientUserId)
  const challenge = mockDb.getById<ChallengeRecord>('challenges', record.challengeId)

  return {
    ...record,
    status: record.status as PartnerRequestStatus,
    sender: sender ? { id: sender.id, displayName: sender.displayName, avatarUrl: sender.avatarUrl } : undefined,
    recipient: recipient ? { id: recipient.id, displayName: recipient.displayName, avatarUrl: recipient.avatarUrl } : undefined,
    challenge: challenge ? {
      id: challenge.id,
      title: challenge.title,
      coverImageUrl: challenge.coverImageUrl,
      soloGroup: challenge.soloGroup as PartnerRequest['challenge'] extends { soloGroup: infer S } ? S : never,
    } : undefined,
  } as PartnerRequest
}
